import { useContext } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Button, ButtonAppearance, Divider, Heading, Icon, Text } from '@atlassian/learning-components';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { media } from '@atlaskit/primitives/compiled';
import DownloadIcon from '@atlaskit/icon/core/download';

import { getAssetUrl } from 'src/services/http/user';
import { type EnrollBannerData } from 'src/types/classSession';
import { generatePdf } from 'src/pages/MyLearningPage/children/utils/generatePdf';
import { GeneratePdfContentType } from 'src/pages/MyLearningPage/types';
import { UserContext } from 'src/context/UserContext';

const COMPLETED_BANNER_TEXT = {
  heading: 'Completed!',
  text: 'Great job! You successfully completed this class. Download your completion certificate below.',
};
const ENROLLED_BANNER_TEXT = {
  heading: "You're enrolled!",
  text: "Download your class materials below and keep an eye on your email for updates. When it's time, use the email link to join the meeting. You can also return to this page.",
};

export const EnrolledBanner = ({
  meetingUrl,
  materialsLink,
  countdown,
  courseName,
  isCompleted,
  completionDate,
}: EnrollBannerData) => {
  const userContext = useContext(UserContext);
  const userName = userContext?.user?.name || '';

  const onJoinMeetingClick = () => {
    window.open(meetingUrl, '_blank');
  };

  const onSaveClick = async () => {
    const downloadUrl = materialsLink.startsWith('https://') ? materialsLink : `https://${materialsLink}`;
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'course-materials.pdf';
    link.click();
    URL.revokeObjectURL(url);
  };

  const onCompleteCertificateClick = () =>
    generatePdf({
      title: courseName,
      completionDate: completionDate || new Date().toISOString(),
      userName,
      contentType: GeneratePdfContentType.COURSE,
    });

  const HeadingText = isCompleted ? COMPLETED_BANNER_TEXT.heading : ENROLLED_BANNER_TEXT.heading;
  const TextContent = isCompleted ? COMPLETED_BANNER_TEXT.text : ENROLLED_BANNER_TEXT.text;

  return (
    <div css={bannerContainerStyles}>
      <img
        src={getAssetUrl('/images/contentBanners/enrolledBanner.svg')}
        alt="New Enroll Banner"
        css={bannerImgStyles}
      />
      <div css={headerStyles}>
        <Heading level={3}>{HeadingText}</Heading>
        <Text>{TextContent}</Text>
      </div>
      <Divider color={token('color.border')} />
      <div css={contentContainerStyles}>
        {materialsLink && (
          <div css={materialsStackStyles}>
            <Text>Download your class materials below:</Text>
            <Button
              appearance={ButtonAppearance.LINK}
              iconBefore={<DownloadIcon color={token('color.text.brand')} label="download materials" />}
              label="Course materials"
              onClick={onSaveClick}
            />
          </div>
        )}
        {isCompleted ? (
          <Button
            label="Completion certificate"
            appearance={ButtonAppearance.SECONDARY}
            iconAfter={<Icon type="download" />}
            onClick={onCompleteCertificateClick}
          />
        ) : (
          <div css={countdownContainerStyles}>
            <div css={countdownStyles}>
              <Text size="regular">
                Class starts in:
                <span css={countdownValueStyles}>{` ${countdown.days} days ${countdown.hours} hours`}</span>
              </Text>
            </div>
            <Button
              appearance={ButtonAppearance.PRIMARY}
              iconAfter={<ArrowRightIcon label="" />}
              label="Join meeting"
              onClick={onJoinMeetingClick}
              disabled={!countdown.isLessThanThirtyMinutes}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const bannerContainerStyles = css({
  display: 'flex',
  width: '100%',
  paddingBottom: '1.5rem',
  flexDirection: 'column',
  gap: '1.5rem',
  borderRadius: '8px',
  boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 1px 1px 0 rgba(30, 31, 33, 0.25)',
});

const bannerImgStyles = css({
  width: '100%',
  maxHeight: '1rem',
  minHeight: '1rem',
  objectFit: 'cover',
  borderTopLeftRadius: 'inherit',
  borderTopRightRadius: 'inherit',
});

const headerStyles = css({
  padding: '0 1.5rem',
  textAlign: 'center',
  maxWidth: '61.25rem',
  margin: '0 auto',
});

const contentContainerStyles = css({
  display: 'flex',
  padding: '0 1.5rem',
  justifyContent: 'space-between',
  alignItems: 'flex-end',

  [media.below.sm]: {
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'flex-start',
  },
});

const materialsStackStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '.5rem',
});

const countdownContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',

  [media.above.sm]: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
  },
});

const countdownStyles = css({
  display: 'flex',
  padding: '0.25rem 0.75rem',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '0.25rem',
  border: `1px solid ${token('color.border')}`,
});

const countdownValueStyles = css({
  color: token('color.text'),
  fontFamily: 'Inter',
  fontSize: '1.25rem',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '2rem',
});
